/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/shipping-and-fulfillment/fulfillmentorder
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyFulfillmentOrderKey,
  ShopifyFulfillmentOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey,
  ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemToProductVariantKey,
  ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
  ShopifyFulfillmentOrderLineItemToShopKey,
  ShopifyInventoryItemKey,
  ShopifyOrderLineItemKey,
  ShopifyProductVariantKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyFulfillmentOrderLineItemKey,
  "2022-01": {
    name: "Shopify Fulfillment Order Line Item",
    fields: [
      {
        name: "Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_order_id" } },
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderKey,
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_item_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Remaining Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillable_quantity" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_sublist", resource: "fulfillmentOrder", parent: "order" },
  },
  "2023-04": {
    fields: [
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "inventory_item_id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
      },
    ],
  },
  "2024-07": {
    fields: [
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "fulfillment_order_id",
            graphQLFieldKey: "fulfillmentOrderId",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderKey,
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "lineItem", graphQLSelection: "id", shopifyFieldKey: "line_item_id" },
        },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Remaining Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "fulfillable_quantity", graphQLFieldKey: "remainingQuantity" },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity", graphQLFieldKey: "totalQuantity" } },
      },
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "inventory_item_id",
            graphQLFieldKey: "inventoryItemId",
          },
        },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentOrderLineItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_id",
            graphQLFieldKey: "variant",
            graphQLSelection: "id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Image",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "image",
            graphQLSelection: `
          width
          height
          id
          altText
          url
          `,
            shopifyFieldKey: "image",
          },
        },
      },
      {
        name: "Financial Summaries",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "financialSummaries",
            graphQLSelection: `
          approximateDiscountedUnitPriceSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          discountAllocations {
            approximateAllocatedAmountPerItem {
              presentmentMoney {
                amount
                currencyCode
              }
              shopMoney {
                amount
                currencyCode
              }
            }
            discountApplication {
              allocationMethod
              targetSelection
              targetType
            }
          }
          originalUnitPriceSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          quantity
          `,
            shopifyFieldKey: "financialSummaries",
          },
        },
      },
      {
        name: "Product Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "productTitle", shopifyFieldKey: "productTitle" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "requiresShipping", shopifyFieldKey: "requiresShipping" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sku", shopifyFieldKey: "sku" } },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "variantTitle", shopifyFieldKey: "variantTitle" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "vendor", shopifyFieldKey: "vendor" } },
      },
      {
        name: "Warnings",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "warnings",
            graphQLSelection: `
          description
          title
          `,
            shopifyFieldKey: "warnings",
          },
        },
      },
      {
        name: "Weight",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "weight",
            graphQLSelection: `
          unit
          value
          `,
            shopifyFieldKey: "weight",
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "fulfillmentOrder.lineItems",
      parent: "fulfillmentOrders",
    },
  },
};

export const ShopifyFulfillmentOrderLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
